// Shared look & feel for the Invoices screens — tokens and small pieces used
// by both the list and the detail, matching the Nami lavender design handoff.
import { Box } from '@mui/material'

export const T = {
	font: "'Outfit', sans-serif",
	bg: '#FBFAFE',
	surface: '#F5F3FB',
	field: '#FFFFFF',
	border: '#E7E2F3',
	divider: '#F0EDF9',
	rowDivider: '#F5F3FB',
	text: '#2E2A40',
	soft: '#6E6886',
	muted: '#8B85A4',
	faint: '#B4ADC6',
	breed: '#9A93B4',
	primary: '#7C6BD9',
	primaryDeep: '#5F4EC2',
	primarySoft: '#EDE9FB',
	warnText: '#8F6217',
	warnBg: '#FDF3E4',
	okText: '#25714A',
	okBg: '#E9F6EE',
	okIcon: '#2F8A5B',
	voidText: '#6E6889',
	voidBg: '#EFEDF5',
	danger: '#BE4B45',
	shadow: '0 1px 3px rgba(46,42,64,0.04)',
	btnShadow: '0 2px 8px rgba(124,107,217,0.28)',
}

export const PILL_COLORS = {
	unpaid: { bg: T.warnBg, color: T.warnText, label: 'Unpaid' },
	paid: { bg: T.okBg, color: T.okText, label: 'Paid' },
	upcoming: { bg: T.primarySoft, color: T.primaryDeep, label: 'Upcoming' },
	void: { bg: T.voidBg, color: T.voidText, label: 'Void' },
}

export function StatusPill({ status, small }) {
	const c = PILL_COLORS[status] || PILL_COLORS.void
	return (
		<Box component="span" sx={{
			display: 'inline-flex', alignItems: 'center', height: small ? 19 : 22, px: small ? '8px' : '9px',
			borderRadius: '99px', bgcolor: c.bg, color: c.color,
			fontSize: small ? '11px' : '11.5px', fontWeight: 750, letterSpacing: '0.02em', whiteSpace: 'nowrap',
			fontFamily: T.font,
		}}>
			{c.label}
		</Box>
	)
}

export function PawIcon({ size = 16 }) {
	return (
		<svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
			<ellipse cx="7.2" cy="8.4" rx="2.1" ry="2.7" transform="rotate(-18 7.2 8.4)" />
			<ellipse cx="16.8" cy="8.4" rx="2.1" ry="2.7" transform="rotate(18 16.8 8.4)" />
			<ellipse cx="3.6" cy="13.2" rx="1.8" ry="2.3" transform="rotate(-38 3.6 13.2)" />
			<ellipse cx="20.4" cy="13.2" rx="1.8" ry="2.3" transform="rotate(38 20.4 13.2)" />
			<path d="M12 11.2c2.9 0 5.6 2.4 5.6 5.1 0 1.9-1.4 3.2-3.2 3.2-.9 0-1.7-.3-2.4-.3s-1.5.3-2.4.3c-1.8 0-3.2-1.3-3.2-3.2 0-2.7 2.7-5.1 5.6-5.1z" />
		</svg>
	)
}

// 16x16 hand-drawn-style stroke icons, same system as the design handoff
export function InvIcon({ name, size = 15, sw = 1.7 }) {
	const P = {
		receipt: <><path d="M3.5 1.5h9v13l-1.5-1.1-1.5 1.1-1.5-1.1-1.5 1.1-1.5-1.1-1.5 1.1v-13z" /><path d="M6 5.3h4M6 8h4M6 10.7h2.5" /></>,
		search: <><circle cx="7" cy="7" r="4.6" /><path d="M10.5 10.5L14 14" /></>,
		back: <path d="M10 3.5L5.5 8l4.5 4.5" />,
		chevRight: <path d="M6 3.5L10.5 8 6 12.5" />,
		card: <><rect x="1.5" y="3.5" width="13" height="9" rx="1.5" /><path d="M1.5 6.5h13" /></>,
		calendar: <><rect x="2" y="3" width="12" height="11" rx="2" /><path d="M2 6.5h12M5.5 1.5v3M10.5 1.5v3" /></>,
		tick: <path d="M3 8.5l3.2 3L13 4.5" />,
		download: <path d="M8 2.5v8M4.8 7.3L8 10.5l3.2-3.2M2.5 13.5h11" />,
		tickCircle: <><circle cx="8" cy="8" r="6" /><path d="M5.3 8.2l1.9 1.9 3.5-3.8" /></>,
		voidx: <><circle cx="8" cy="8" r="6" /><path d="M3.8 3.8l8.4 8.4" /></>,
		warn: <><path d="M8 2.4L14.4 13.2H1.6L8 2.4z" /><path d="M8 6.8v3M8 11.4v.01" /></>,
		retry: <><path d="M12.5 6.5A4.8 4.8 0 003.9 4.6" /><path d="M3.5 1.6v3.2h3.2" /><path d="M3.5 9.5a4.8 4.8 0 008.6 1.9" /><path d="M12.5 14.4v-3.2H9.3" /></>,
		lock: <><rect x="3.5" y="7" width="9" height="7" rx="1.5" /><path d="M5.5 7V5a2.5 2.5 0 015 0v2" /></>,
		x: <path d="M4 4l8 8M12 4l-8 8" />,
		plus: <path d="M8 3.5v9M3.5 8h9" />,
	}
	return (
		<svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke="currentColor"
			strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: 'none' }}>
			{P[name] || null}
		</svg>
	)
}

export function ClientAvatar({ name, size = 33 }) {
	const initials = (name || '?').split(' ').map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase()
	return (
		<Box component="span" sx={{
			width: size, height: size, flex: 'none', borderRadius: '50%',
			bgcolor: T.primarySoft, color: T.primaryDeep,
			display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
			fontSize: '12.5px', fontWeight: 800, letterSpacing: '0.02em', fontFamily: T.font,
		}}>
			{initials}
		</Box>
	)
}

// Ensure the Outfit font is available (same pattern as LogPaymentDialog)
export function ensureOutfitFont() {
	const id = 'outfit-font-link'
	if (!document.getElementById(id)) {
		const link = document.createElement('link')
		link.id = id
		link.rel = 'stylesheet'
		link.href = 'https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&display=swap'
		document.head.appendChild(link)
	}
}

export const card = {
	bgcolor: T.field,
	border: `1px solid ${T.border}`,
	borderRadius: '14px',
	boxShadow: T.shadow,
}

export const ghostBtn = {
	display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '7px',
	border: `1.5px solid ${T.border}`, borderRadius: '10px', bgcolor: T.field, color: T.text,
	fontSize: '13px', fontWeight: 650, fontFamily: T.font, whiteSpace: 'nowrap', cursor: 'pointer',
	textTransform: 'none',
	'&:hover': { borderColor: T.primary, color: T.primaryDeep, bgcolor: T.field },
}

export const primaryBtn = {
	display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
	border: 'none', borderRadius: '10px', bgcolor: T.primary, color: '#FFFFFF',
	fontSize: '13.5px', fontWeight: 700, fontFamily: T.font, whiteSpace: 'nowrap', cursor: 'pointer',
	boxShadow: T.btnShadow, textTransform: 'none',
	'&:hover': { bgcolor: T.primaryDeep },
}

export const overline = {
	fontSize: '10.5px', fontWeight: 800, textTransform: 'uppercase',
	letterSpacing: '0.06em', color: T.muted, fontFamily: T.font,
}
